<template>
	<section class="teacher-resource-section" :aria-label="t(`${I18N_NS}.teacherResource.ariaLabel`)">
		<h2 class="teacher-resource-section__title">{{ t(`${I18N_NS}.teacherResource.title`) }}</h2>

		<div v-if="loading && !teacherList.length" class="teacher-resource-section__status">
			{{ t(`${I18N_NS}.teacherResource.loading`) }}
		</div>
		<div v-else-if="!teacherList.length" class="teacher-resource-section__status">
			{{ t(`${I18N_NS}.teacherResource.empty`) }}
		</div>

		<div v-else class="teacher-resource-section__scroll">
			<article
				v-for="teacher in teacherList"
				:key="getTeacherUid(teacher)"
				:ref="el => setCardRef(el, teacher)"
				class="teacher-resource-card"
				@click="handleDetailClick(teacher)"
			>
				<div class="teacher-resource-card__media">
					<img
						class="teacher-resource-card__avatar"
						:src="teacher?.avatar"
						:alt="teacher?.name || t(`${I18N_NS}.teacherResource.avatarAlt`)"
					/>
					<div class="teacher-resource-card__meta-group">
						<div class="teacher-resource-card__meta teacher-resource-card__meta--score">
							<img :src="starIcon" alt="" aria-hidden="true" />
							<span>{{ teacher?.displayScore }}</span>
						</div>
						<div class="teacher-resource-card__meta teacher-resource-card__meta--like">
							<img :src="likeIcon" alt="" aria-hidden="true" />
							<span>{{ teacher?.displayLikes }}</span>
						</div>
					</div>
				</div>

				<!-- <button
					v-if="teacher.showChatBubble"
					class="teacher-resource-card__chat"
					type="button"
					:aria-label="t(`${I18N_NS}.teacherResource.chatAria`)"
					@click.stop="handleConsultClick(teacher)"
				>
					<img :src="chatIcon" alt="" aria-hidden="true" />
				</button> -->

				<div class="teacher-resource-card__content">
					<h3 class="teacher-resource-card__name">{{ teacher?.name }}</h3>
					<div class="teacher-resource-card__badges">
						<span
							v-for="(badge, badgeIndex) in (teacher?.highlightBadges || []).slice(0, 3)"
							:key="badge?.text || `${badgeIndex}`"
							class="teacher-resource-card__badge"
							:style="getBadgeStyle(badge, badgeIndex)"
						>
							{{ badge?.text || "" }}
						</span>
					</div>
					<p class="teacher-resource-card__summary">{{ teacher?.summaryText }}</p>
					<button
						class="teacher-resource-card__button"
						type="button"
						@click.stop="handleCardAction(teacher)"
					>
						{{ teacher?.actionText }}
					</button>
				</div>
			</article>
		</div>
	</section>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { getTeacherAskRecommendList } from "~/api/api";
import { useMasterFortuneMobilePageData } from "@variants/seer/master-fortune/composables/useMasterFortunePageData";
import { useI18n } from "~/composables/useI18n";
import { useMasterFortuneTeacherNav } from "@variants/seer/master-fortune/composables/useMasterFortuneTeacherNav";
import {
	getConsultationCountBucket,
	getMentorTypeByAreaType,
	getPriceBucket,
	normalizeMentorRating,
	useMentorListTracking,
} from "@variants/seer/master-fortune/composables/useMentorListTracking";
import likeIcon from "../../../images/like.png";
import starIcon from "../../../images/star.png";
import chatIcon from "../../../pc/components/teacher-resource-section/images/chat.png";

const I18N_NS = "masterFortune";
const RESOURCE_SIZE = 12;
const TEACHER_DETAIL_AREA_TYPE = 10;

const { t, trackTranslationDeps } = useI18n();
const { track } = useMentorListTracking({
	deviceType: "mobile",
	mentorType: "normal",
});
const { toChat, toTeacherDetail } = useMasterFortuneTeacherNav();
const pageData = useMasterFortuneMobilePageData();

const teacherListData = ref([]);
const loading = ref(false);
const cardRefs = new Map();
const impressedMentorIds = new Set();
const impressionTimers = new Map();
let cardObserver = null;

const teacherList = computed(() => {
	trackTranslationDeps();
	return (teacherListData.value || []).map(formatTeacherResource);
});

function getTeacherUid(teacher) {
	return teacher?.teacher_uid || teacher?.uid || teacher?.id || "";
}

function isDetailOnlyTeacher(teacher) {
	return Number(teacher?.area_type) === TEACHER_DETAIL_AREA_TYPE;
}

function formatTeacherResource(teacher) {
	const labels = normalizeLabels(teacher?.label);
	const detailOnly = isDetailOnlyTeacher(teacher);

	return {
		...teacher,
		teacher_uid: getTeacherUid(teacher),
		avatar: teacher?.avatar || "",
		name: teacher?.nickname || t(`${I18N_NS}.teacherResource.defaultName`),
		displayScore: teacher?.score || "5.0",
		displayLikes: teacher?.answers ?? 0,
		highlightBadges: labels.length ? labels : getDefaultBadges(),
		summaryText:
			teacher?.introduce || teacher?.brief_intro || t(`${I18N_NS}.teacherResource.defaultSummary`),
		showChatBubble: !detailOnly,
		actionText: detailOnly
			? t(`${I18N_NS}.teacherResource.viewDetail`)
			: t(`${I18N_NS}.teacherResource.consultNow`),
	};
}

function getDefaultBadges() {
	trackTranslationDeps();
	return [0, 1].map(index => ({
		text: t(`${I18N_NS}.teacherResource.defaultTags.${index}`),
		textColor: "",
		bgColor: "",
	}));
}

function normalizeLabels(labels) {
	if (!Array.isArray(labels)) return [];
	return labels
		.map(item => ({
			text: item?.text || "",
			textColor: numberColorToCss(item?.text_color),
			bgColor: numberColorToCss(item?.color),
		}))
		.filter(item => item.text);
}

function numberColorToCss(value) {
	const num = Number(value);
	if (!Number.isFinite(num) || num < 0) return "";
	const hex = num.toString(16).padStart(8, "0");
	const alpha = parseInt(hex.slice(0, 2), 16) / 255;
	const r = parseInt(hex.slice(2, 4), 16);
	const g = parseInt(hex.slice(4, 6), 16);
	const b = parseInt(hex.slice(6, 8), 16);
	if (alpha >= 0.999) return `#${hex.slice(2)}`;
	return `rgba(${r}, ${g}, ${b}, ${alpha.toFixed(3)})`;
}

function getBadgeStyle(badge, badgeIndex) {
	const fallbackStyles = [
		{ background: "#fff3f0", color: "#f05221" },
		{ background: "#fff9ea", color: "#f2af19" },
		{ background: "#fff9ea", color: "#f2af19" },
	];
	const fallback = fallbackStyles[badgeIndex] || fallbackStyles[1];
	return {
		background: badge?.bgColor || fallback.background,
		color: badge?.textColor || fallback.color,
	};
}

async function loadTeachers() {
	if (loading.value) return;
	loading.value = true;
	try {
		const res = await getTeacherAskRecommendList({
			page: 1,
			size: RESOURCE_SIZE,
		});
		teacherListData.value = res?.data || [];
	} catch (error) {
		console.error("getTeacherAskRecommendList error:", error);
	} finally {
		loading.value = false;
	}
}

function handleCardAction(teacher) {
	const teacherUid = getTeacherUid(teacher);
	if (!teacherUid) return;
	if (isDetailOnlyTeacher(teacher)) {
		toTeacherDetail(teacherUid);
		return;
	}
	handleConsultClick(teacher);
}

function handleConsultClick(teacher) {
	const teacherUid = getTeacherUid(teacher);
	if (!teacherUid) return;
	track("mentor_consult_click", {
		mentor_id: teacherUid,
		mentor_type: getMentorTypeByAreaType(teacher?.area_type),
		section_id: "recommended_mentor",
		price_bucket: getPriceBucket(teacher?.displayPrice),
	});
	toChat(teacherUid);
}

function handleDetailClick(teacher) {
	const teacherUid = getTeacherUid(teacher);
	if (!teacherUid) return;
	track("mentor_detail_click", {
		mentor_id: teacherUid,
		mentor_type: getMentorTypeByAreaType(teacher?.area_type),
		section_id: "recommended_mentor",
		position: teacherList.value.findIndex(item => getTeacherUid(item) === teacherUid) + 1,
	});
	toTeacherDetail(teacherUid);
}

const setCardRef = (el, teacher) => {
	const id = getTeacherUid(teacher);
	if (!id) return;
	if (el) cardRefs.set(id, el);
	else cardRefs.delete(id);
};

function refreshCardObserver() {
	if (!import.meta.client) return;
	cardObserver?.disconnect();
	cardObserver = new IntersectionObserver(
		entries => {
			entries.forEach(entry => {
				const mentorId = entry.target.dataset.mentorId;
				if (!mentorId || impressedMentorIds.has(mentorId)) return;
				if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
					if (impressionTimers.has(mentorId)) return;
					const timer = window.setTimeout(() => {
						const teacher = teacherList.value.find(item => getTeacherUid(item) === mentorId);
						if (!teacher || impressedMentorIds.has(mentorId)) return;
						impressedMentorIds.add(mentorId);
						track("recommended_mentor_impression", {
							mentor_id: mentorId,
							mentor_type: getMentorTypeByAreaType(teacher?.area_type),
							position: teacherList.value.findIndex(item => getTeacherUid(item) === mentorId) + 1,
							rating: normalizeMentorRating(teacher?.displayScore),
							consultation_count_bucket: getConsultationCountBucket(teacher?.displayLikes),
						});
					}, 1000);
					impressionTimers.set(mentorId, timer);
				} else if (impressionTimers.has(mentorId)) {
					window.clearTimeout(impressionTimers.get(mentorId));
					impressionTimers.delete(mentorId);
				}
			});
		},
		{ threshold: [0.5] }
	);

	teacherList.value.forEach(teacher => {
		const mentorId = getTeacherUid(teacher);
		const el = cardRefs.get(mentorId);
		if (!el) return;
		el.dataset.mentorId = mentorId;
		cardObserver.observe(el);
	});
}

watch(
	() => pageData.value?.resourceTeachers,
	teachers => {
		if (teachers?.length && !teacherListData.value.length) {
			teacherListData.value = teachers;
		}
	},
	{ immediate: true }
);

onMounted(async () => {
	if (!teacherListData.value.length) {
		await loadTeachers();
	}
	nextTick(refreshCardObserver);
});

watch(
	() => teacherList.value.length,
	() => nextTick(refreshCardObserver)
);

onBeforeUnmount(() => {
	impressionTimers.forEach(timer => window.clearTimeout(timer));
	impressionTimers.clear();
	cardObserver?.disconnect();
});
</script>

<style lang="scss" scoped>
.teacher-resource-section {
	padding: 24px 0 0;

	&__title {
		margin: 0 0 24px;
		padding: 0 32px;
		font-weight: 700;
		font-size: 40px;
		line-height: 50px;
		color: #333333;
	}

	&__status {
		padding: 48px 32px;
		text-align: center;
		font-size: 24px;
		line-height: 34px;
		color: #999999;
	}

	&__scroll {
		display: flex;
		gap: 24px;
		padding: 0 32px 8px;
		overflow-x: auto;
		overflow-y: hidden;
		-webkit-overflow-scrolling: touch;
		scrollbar-width: none;

		&::-webkit-scrollbar {
			display: none;
		}
	}
}

.teacher-resource-card {
	position: relative;
	flex: 0 0 484px;
	width: 534px;
	background: transparent;
	border: none;
	border-radius: 0;
	overflow: visible;

	&__media {
		position: relative;
		height: 302px;
		background: #fff7f4;
		border-radius: 12px 12px 0 0;
		overflow: hidden;
	}

	&__avatar {
		position: absolute;
		left: 50%;
		bottom: 0;
		width: 82%;
		max-height: 92%;
		object-fit: contain;
		transform: translateX(-50%);
	}

	&__meta-group {
		position: absolute;
		top: 10px;
		left: 10px;
		display: flex;
		align-items: stretch;
		overflow: hidden;
		border-radius: 12px 0 12px 0;
	}

	&__meta {
		display: inline-flex;
		align-items: center;
		gap: 4px;
		height: 40px;
		padding: 0 8px;
		font-size: 22px;
		line-height: 1;
		white-space: nowrap;

		img {
			width: 24px;
			height: 24px;
			flex-shrink: 0;
		}
	}

	&__meta--score {
		background: #ffffff;
		color: #333333;
	}

	&__meta--like {
		background: rgba(0, 0, 0, 0.3);
		color: #ffffff;
	}

	&__chat {
		position: absolute;
		right: 58px;
		top: 262px;
		z-index: 2;
		display: flex;
		align-items: center;
		justify-content: center;
		width: 80px;
		height: 80px;
		padding: 0;
		border: none;
		border-radius: 50%;
		background: rgba(49, 49, 49, 0.92);
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
		cursor: pointer;

		img {
			width: 40px;
			height: 40px;
			object-fit: contain;
		}
	}

	&__content {
		padding: 22px 24px 24px;
		background: #ffffff;
		border: 1px solid #efefef;
		border-top: none;
		border-radius: 0 0 12px 12px;
	}

	&__name {
		margin: 0;
		font-weight: 700;
		font-size: 30px;
		line-height: 44px;
		color: #333333;
		overflow: hidden;
		white-space: nowrap;
		text-overflow: ellipsis;
	}

	&__badges {
		display: flex;
		flex-wrap: nowrap;
		gap: 8px;
		margin-top: 12px;
		overflow: hidden;
	}

	&__badge {
		flex-shrink: 0;
		padding: 2px 12px;
		border-radius: 4px;
		font-size: 22px;
		line-height: 32px;
		white-space: nowrap;
	}

	&__summary {
		margin: 16px 0 0;
		font-size: 24px;
		line-height: 36px;
		color: #333333;
		display: -webkit-box;
		-webkit-line-clamp: 2;
		line-clamp: 2;
		-webkit-box-orient: vertical;
		overflow: hidden;
	}

	&__button {
		width: 100%;
		height: 60px;
		margin-top: 24px;
		border: 0;
		border-radius: 4px;
		background: #333333;
		font-weight: 500;
		font-size: 28px;
		line-height: 60px;
		color: #ffffff;
		cursor: pointer;
	}
}
</style>
